import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Text, View, Pressable } from 'react-native';
import { useQuery, useSubscription, useMutation } from '@apollo/client';
import DataTable from '@preflighttech/preflight-tables';
import styled from 'styled-components/native';
import htmlStyled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faCirclePlus,
  faCopy,
  faEdit,
  faEye,
  faMoneyCheck,
} from '@fortawesome/free-solid-svg-icons';
import {
  faClockRotateLeft,
  faPaperPlane,
  faSquareList,
} from '@fortawesome/pro-regular-svg-icons';
import moment from 'moment';
import isEqual from 'react-fast-compare';

import secrets from 'app/secrets';
import NewBuyer from 'app/src/components/Buyers/new';
import BuyerAutocomplete from '../../Autocomplete/BuyerAutocomplete';
import Fees from 'app/src/components/Fees';
import ResourceNotifications from 'app/src/components/Notifications/Resource';
import NewInvoice from 'app/src/components/Accounting/Invoices/new';
import Invoice from 'app/src/components/Accounting/Invoices/show';
import SaleHistory from 'app/src/components/Assets/SaleHistory';
import Modal from 'app/src/elements/Modal';
import DataState from 'app/src/components/DataState';
import AuthContext from 'app/src/contexts/AuthContext';
import { Link } from 'app/src/utils/routing';
import { currency } from './currency';
import formattedLocation from 'app/src/components/Assets/formattedLocation';
import { UPDATE_ASSET_RELEASE_SENT } from 'app/src/components/Assets/queries';

import {
  SALE_ASSET_UPDATED,
  SALE_ASSETS,
  UPDATE_SALE_ASSET,
  ADD_TO_ACH_BATCH,
} from './queries';
import BidFunctions from './BidFunctions';
import PaddleServiceBids from './PaddleServiceBids';
import SaleAssetField from './SaleAssetField';
import TitleLogHistory from '../../TitleLogs/TitleLogHistory';
import EditStatus from './EditStatus';
import UnmarkNoSale from './UnmarkNoSale';
import './style.css';

const ALL_LOT_NUMBERS =
  Array.from(
    { length: 999 },
    (_, index) => String(index + 1)
  );

export const LOT_NUMBER_YEARS =
  Array.from(
    { length: new Date().getFullYear() + 1 - 2008 },
    (_, index) => String(index + 9).padStart(2, '0')
  );

export const lotSuffixesMap = lotPrefixes => {
  const retval = {};

  lotPrefixes.forEach(({ code }) => {
    retval[`${code}.NONE`] = [...ALL_LOT_NUMBERS];

    LOT_NUMBER_YEARS.forEach(year => {
      retval[`${code}.${year}`] = [...ALL_LOT_NUMBERS];
    });
  });

  return retval;
};

export const lotSuffixOptions = (lotSuffixMap, prefix, year, saleAssets) => {
  if (!prefix) { return []; }

  year = year ? parseInt(year, 10) : null;
  const yearCode = year ? String(year).padStart(2, '0') : 'NONE';

  const available = lotSuffixMap[`${prefix}.${yearCode}`] || [];

  const used = saleAssets.filter(saleAsset => (
    saleAsset.lotNumberPrefix === prefix &&
    (
      parseInt(saleAsset.lotNumberYear, 10) === year ||
      saleAsset.lotNumberYear === year
    ) &&
    saleAsset.lotNumberSuffix
  )).map(saleAsset => String(saleAsset.lotNumberSuffix));

  return available.filter(suffix => !used.includes(suffix));
};

export { currency } from './currency';

const sortSaleAssetsForDisplay = (saleAssets, isPublicAuctionPreSale) => {
  if (!isPublicAuctionPreSale) {
    return saleAssets;
  }

  return [...saleAssets].sort((a, b) => {
    const aOrder = a.manualRunOrder;
    const bOrder = b.manualRunOrder;

    if (aOrder == null && bOrder == null) {
      return (a.id || '').localeCompare(b.id || '');
    }

    if (aOrder == null) { return 1; }
    if (bOrder == null) { return -1; }

    if (aOrder !== bOrder) {
      return aOrder - bOrder;
    }

    return (a.id || '').localeCompare(b.id || '');
  });
};

const Cell = styled(View)`
  flex-direction: row;
  margin-left: 6px;
  margin-right: 6px;
  justify-content: space-between;
`;

const Value = styled(Text)`
  margin-left: 6px;
  margin-right: 6px;
  white-space: nowrap;
`;

const TableContainer = htmlStyled.div`
  overflow: scroll;
  height: calc(100vh - 560px);
  padding-bottom: 300px;
  position: relative;
`;

const RunOrderBar = htmlStyled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  margin-bottom: 12px;
  background: #fff8dc;
  border: 1px solid #e2c96b;
  border-radius: 6px;
`;

const RunOrderText = htmlStyled.div`
  font-size: 14px;
  font-weight: 600;
`;

const RunOrderInput = htmlStyled.input`
  width: 76px;
  height: 32px;
  padding: 4px 8px;
  border: 1px solid ${props => props.hasError ? '#c62828' : '#c7c7c7'};
  border-radius: 4px;
  font-size: 14px;
  background: ${props => props.disabled ? '#f3f3f3' : '#ffffff'};
`;

const RunOrderError = htmlStyled.div`
  margin-top: 4px;
  color: #c62828;
  font-size: 12px;
  font-weight: 600;
  max-width: 170px;
  line-height: 1.3;
`;

const TABLE_STYLES = {
  table: {
    backgroundColor: 'white',
  },
  header: {
    label: { fontSize: 14 }
  },
  data: {
    cell: {
      fontFamily: 'sans-serif',
      fontSize: 14,
      paddingVertical: 0,
      paddingHorizontal: 0,
    }
  },
};

const humanizeRunOrderError = (message, attemptedValue) => {
  if (!message) {
    return 'Unable to save run order.';
  }

  const normalized = String(message).toLowerCase();

  if (normalized.includes('already') || normalized.includes('taken') || normalized.includes('unique')) {
    return `Run order ${attemptedValue} is already assigned to another asset in this sale.`;
  }

  if (normalized.includes('whole positive integer') || normalized.includes('greater than') || normalized.includes('not a number')) {
    return 'Run order must be a whole positive integer.';
  }

  return message;
};

const SaleProcess = ({ sale, saleId, tableType, linkedSaleAssetId }) => {
  const {
    apolloClient: client, paddleServices, hasPolicy, lotPrefixes
  } = useContext(AuthContext);

  const { data, loading, error, refetch } = useQuery(SALE_ASSETS, { variables: { saleId } });

  const [showNewBuyerModal, setShowNewBuyerModal] = useState(false);
  const [showNewInvoice, setShowNewInvoice] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [showSellerFees, setShowSellerFees] = useState(false);
  const [showSaleHistory, setShowSaleHistory] = useState(false);
  const [showEditStatus, setShowEditStatus] = useState(false);
  const [showPaddleServiceBids, setShowPaddleServiceBids] = useState(false);
  const [showUnmarkNoSale, setShowUnmarkNoSale] = useState(false);
  const [saleAssetId, setSaleAssetId] = useState();
  const [clickedResult, setClickedResult] = useState();
  const [showTitleLogHistory, setShowTitleLogHistory] = useState(false);
  const [clickedTitleLogHistory, setClickedTitleLogHistory] = useState();
  const [clipboard, setClipboard] = useState({});
  const [orderedSaleAssets, setOrderedSaleAssets] = useState([]);
  const [runOrderInputs, setRunOrderInputs] = useState({});
  const [runOrderErrors, setRunOrderErrors] = useState({});
  const [savingRunOrderIds, setSavingRunOrderIds] = useState({});

  const [updateSaleAsset] = useMutation(UPDATE_SALE_ASSET);
  const [updateReleaseSent] = useMutation(UPDATE_ASSET_RELEASE_SENT);
  const [addToAchBatch] = useMutation(ADD_TO_ACH_BATCH);

  const isPublicAuctionPreSale = sale?.publicAuction && 'pre' === tableType;

  const linkedRef = useCallback(element => {
    if (!element || !data?.saleAssets) { return; }

    const rowIndex = data.saleAssets.findIndex(saleAsset => (
      saleAsset.id === linkedSaleAssetId
    ));

    if (rowIndex === -1) { return; }

    const td = element.parentElement.parentElement;
    const tr = td.parentElement;
    const scrollable =
      td.parentElement.parentElement.parentElement.parentElement;

    const y = tr.offsetTop - 32;
    scrollable.scrollTo(0, y);
  }, [data, linkedSaleAssetId]);

  const lotSuffixesAvailable = useMemo(
    () => lotSuffixesMap(lotPrefixes || []),
    [lotPrefixes]
  );

  useEffect(() => {
    if (!data?.saleAssets) { return; }

    const sorted = sortSaleAssetsForDisplay(data.saleAssets, isPublicAuctionPreSale);
    setOrderedSaleAssets(sorted);

    if (isPublicAuctionPreSale) {
      const nextInputs = {};
      sorted.forEach(saleAsset => {
        nextInputs[saleAsset.id] = saleAsset.manualRunOrder ?? '';
      });
      setRunOrderInputs(nextInputs);
    }
  }, [data, isPublicAuctionPreSale]);

  useSubscription(SALE_ASSET_UPDATED, {
    variables: { saleId },
    onSubscriptionData: ({ subscriptionData: { data: subscriptionData } }) => {
      if (!subscriptionData || !subscriptionData.saleAssetUpdated) return;

      const updatedSaleAsset = subscriptionData.saleAssetUpdated.saleAsset;
      const queryResult = client.readQuery({
        query: SALE_ASSETS,
        variables: { saleId }
      });

      if (!queryResult) return;
      const { saleAssets } = queryResult;

      const updatedIndex = saleAssets.findIndex(saleAsset => (
        updatedSaleAsset.id === saleAsset.id
      ));

      if (updatedIndex || updatedIndex === 0) {
        if (isEqual(saleAssets[updatedIndex], updatedSaleAsset)) {
          return;
        }
      }

      let updatedSaleAssets = [
        ...saleAssets.filter(saleAsset => (
          updatedSaleAsset.id !== saleAsset.id
        ))
      ];

      if (!updatedSaleAsset.destroyed) {
        if (updatedIndex || updatedIndex === 0) {
          updatedSaleAssets.splice(updatedIndex, 0, updatedSaleAsset);
        } else {
          updatedSaleAssets = [...updatedSaleAssets, updatedSaleAsset];
        }
      }

      const sortedSaleAssets = sortSaleAssetsForDisplay(
        updatedSaleAssets,
        isPublicAuctionPreSale
      );

      client.writeQuery({
        query: SALE_ASSETS,
        variables: { saleId },
        data: { saleAssets: sortedSaleAssets },
      });

      setOrderedSaleAssets(sortedSaleAssets);

      if (isPublicAuctionPreSale) {
        setRunOrderInputs(current => ({
          ...current,
          [updatedSaleAsset.id]: updatedSaleAsset.manualRunOrder ?? ''
        }));
      }
    }
  });

  const handleRemove = entry => {
    if (window.confirm('Remove from Sale?')) {
      updateSaleAsset({ variables: { id: entry.id, remove: true } });
    }
  };

  const addToBatch = entry => {
    if (window.confirm('Add to Current ACH Batch?')) {
      addToAchBatch({ variables: { id: entry.id } });
    }
  };

  const createRelease = asset => {
    if (window.confirm('Send Release?')) {
      updateReleaseSent({
        variables: {
          id: parseInt(asset?.id, 10),
          releaseSentAt: moment()._d
        }
      });
    }
  };

  const addToClipboard = (saleAsset, type, value) => {
    navigator.clipboard.writeText(value || '');
    setClipboard({ [saleAsset.id]: { value, type, at: new Date().getTime() } });
  };

  const setRunOrderInputValue = (saleAssetId, value) => {
    setRunOrderInputs(current => ({
      ...current,
      [saleAssetId]: value
    }));
  };

  const setRunOrderInputError = (saleAssetId, value) => {
    setRunOrderErrors(current => ({
      ...current,
      [saleAssetId]: value
    }));
  };

  const saveManualRunOrder = async saleAsset => {
    if (!isPublicAuctionPreSale) { return; }

    const rawValue = runOrderInputs[saleAsset.id];
    const trimmedValue = String(rawValue ?? '').trim();

    if (!trimmedValue) {
      setRunOrderInputError(
        saleAsset.id,
        'Run order is required for public auctions.'
      );
      return;
    }

    const nextValue = parseInt(trimmedValue, 10);

    if (
      !Number.isInteger(nextValue) ||
      String(nextValue) !== trimmedValue ||
      nextValue <= 0
    ) {
      setRunOrderInputError(
        saleAsset.id,
        'Run order must be a whole positive integer.'
      );
      return;
    }

    if (nextValue === saleAsset.manualRunOrder) {
      setRunOrderInputError(saleAsset.id, '');
      return;
    }

    setRunOrderInputError(saleAsset.id, '');
    setSavingRunOrderIds(current => ({ ...current, [saleAsset.id]: true }));

    try {
      const result = await updateSaleAsset({
        variables: {
          id: saleAsset.id,
          manualRunOrder: nextValue,
          lotNumberSuffix: nextValue,
          lotNumberYear: null,
        }
      });

      const payload = result?.data?.updateSaleAsset;
      const errors = payload?.errors || [];
      const updatedSaleAsset = payload?.saleAsset;

      if (errors.length > 0 || !updatedSaleAsset) {
        const message = errors.map(entry => entry.message).join(', ');
        setRunOrderInputError(
          saleAsset.id,
          humanizeRunOrderError(message, nextValue)
        );
        return;
      }

      const currentSaleAssets = client.readQuery({
        query: SALE_ASSETS,
        variables: { saleId }
      })?.saleAssets || [];

      const mergedSaleAssets = currentSaleAssets.map(entry => (
        entry.id === updatedSaleAsset.id ? updatedSaleAsset : entry
      ));

      const sortedSaleAssets = sortSaleAssetsForDisplay(
        mergedSaleAssets,
        isPublicAuctionPreSale
      );

      client.writeQuery({
        query: SALE_ASSETS,
        variables: { saleId },
        data: { saleAssets: sortedSaleAssets }
      });

      setOrderedSaleAssets(sortedSaleAssets);
      setRunOrderInputs(current => ({
        ...current,
        [saleAsset.id]: updatedSaleAsset.manualRunOrder ?? ''
      }));
      setRunOrderInputError(saleAsset.id, '');

      await refetch();
    } catch (saveError) {
      const graphQLError =
        saveError?.graphQLErrors?.[0]?.message ||
        saveError?.message ||
        'Unable to save run order.';

      setRunOrderInputError(
        saleAsset.id,
        humanizeRunOrderError(graphQLError, nextValue)
      );
    } finally {
      setSavingRunOrderIds(current => ({ ...current, [saleAsset.id]: false }));
    }
  };

  const handleRunOrderKeyDown = saleAsset => async event => {
    if ('Enter' === event.key) {
      event.preventDefault();
      await saveManualRunOrder(saleAsset);
      event.target.blur();
    }
  };

  if (loading || !paddleServices) { return <DataState.Loading />; }
  if (error) { return <DataState.Error error={error} />; }

  const saleAssets = orderedSaleAssets.length ? orderedSaleAssets : (data?.saleAssets || []);

  const columns = [
    {
      key: 'id',
      label: 'ID',
      hidden: true,
      content: ({ value }) => <Value>{value}</Value>,
    },
    {
      key: 'lotNumberSort',
      label: 'Lot #',
      headerStyle: { maxWidth: 90 },
      value: ({ entry }) => entry.correctedLotNumber,
      sort: 'prevent',
      content: ({ entry }) => {
        const linkedStyle = entry.isLinked ?
          {
            backgroundColor: 'yellow',
            minHeight: 33,
            flexDirection: 'row',
            alignItems: 'center',
          } :
          {};

        if ('pre' !== tableType) {
          return (
            <View style={linkedStyle}>
              <span ref={entry.isLinked ? linkedRef : null}></span>
              <Value>{entry.correctedLotNumber}</Value>
            </View>
          );
        }

        if (isPublicAuctionPreSale) {
          return (
            <div
              className={entry.isLinked ? 'linked-row' : ''}
              style={{
                ...linkedStyle,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center'
              }}
            >
              <span ref={entry.isLinked ? linkedRef : null}></span>
              <SaleAssetField
                saleAsset={entry}
                field="lotNumberPrefix"
                selectOptions={lotPrefixes.map(prefix => prefix.code)}
              />
              <Text>-</Text>
              <Value>{entry.manualRunOrder || ''}</Value>
            </div>
          );
        }

        return (
          <div
            className={entry.isLinked ? 'linked-row' : ''}
            style={{
              ...linkedStyle,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'baseline'
            }}
          >
            <span ref={entry.isLinked ? linkedRef : null}></span>
            <SaleAssetField
              saleAsset={entry}
              field="lotNumberPrefix"
              selectOptions={lotPrefixes.map(prefix => prefix.code)}
            />
            <Text>-</Text>
            <SaleAssetField
              saleAsset={entry}
              field="lotNumberYear"
              selectOptions={LOT_NUMBER_YEARS}
              isInt
            />
            <SaleAssetField
              saleAsset={entry}
              field="lotNumberSuffix"
              selectOptions={[
                entry.lotNumberSuffix,
                ...lotSuffixOptions(
                  lotSuffixesAvailable,
                  entry.lotNumberPrefix,
                  entry.lotNumberYear,
                  saleAssets
                )
              ]}
              isInt
            />
          </div>
        );
      }
    },
    {
      key: 'manualRunOrder',
      label: 'Run Order',
      hidden: !isPublicAuctionPreSale,
      value: ({ entry }) => entry.manualRunOrder,
      sort: 'prevent',
      content: ({ entry }) => {
        const inputValue = runOrderInputs[entry.id] ?? '';
        const errorMessage = runOrderErrors[entry.id];
        const saving = !!savingRunOrderIds[entry.id];

        return (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <RunOrderInput
              type="number"
              min="1"
              step="1"
              value={inputValue}
              hasError={!!errorMessage}
              disabled={saving}
              onChange={e => {
                setRunOrderInputValue(entry.id, e.target.value);
                if (runOrderErrors[entry.id]) {
                  setRunOrderInputError(entry.id, '');
                }
              }}
              onBlur={() => saveManualRunOrder(entry)}
              onKeyDown={handleRunOrderKeyDown(entry)}
            />
            {errorMessage ? (
              <RunOrderError>{errorMessage}</RunOrderError>
            ) : null}
          </div>
        );
      }
    },
    {
      key: 'lotOrder',
      label: 'Lot Order',
      hidden: 'pre' !== tableType || isPublicAuctionPreSale,
      value: ({ entry }) => entry.lotNumberSort,
      sort: 'prevent',
      content: ({ entry, value }) => (
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between'
          }}
        >
          <Value>{value}</Value>
          <Pressable onPress={() => addToClipboard(entry, 'lotOrder', value)}>
            <FontAwesomeIcon
              icon={faCopy}
              title="Copy to Clipboard"
              color={
                (entry.clipboardLotOrder &&
                  entry.clipboardLotOrder.at + 5000 > new Date().getTime()
                ) ? 'green' : 'black'
              }
            />
          </Pressable>
        </View>
      )
    },
    {
      key: 'stockNumber',
      label: 'Stock #',
      value: ({ entry }) => entry.asset.stockNumber,
      content: ({ entry, value }) => (
        <View
          style={{
            flexDirection: 'row',
            gap: 2,
          }}
        >
          <Link
            to={`/sellers/${entry.asset.seller.id}/assets/${entry.asset.id}`}
          >
            <Value>{entry.asset.stockNumber}</Value>
          </Link>
          {'pre' === tableType && (
            <Pressable
              onPress={() => addToClipboard(entry, 'stockNumber', value)}
            >
              <FontAwesomeIcon
                icon={faCopy}
                title="Copy to Clipboard"
                color={
                  (entry.clipboardStockNumber &&
                    entry.clipboardStockNumber.at + 5000 > new Date().getTime()
                  ) ? 'green' : 'black'
                }
              />
            </Pressable>
          )}
        </View>
      )
    },
    {
      key: 'clientNumber',
      label: 'Client #',
      value: ({ entry }) => entry.asset.clientNumber,
      content: ({ value }) => (
        <Value>{value}</Value>
      )
    },
    {
      key: 'enteredOn',
      label: 'Date',
      hidden: 'pre' !== tableType,
      content: ({ entry }) => (
        <SaleAssetField
          saleAsset={entry}
          field="enteredOn"
          isDate
        />
      )
    },
    {
      key: 'addOn',
      label: 'Add Date',
      hidden: 'pre' !== tableType,
      content: ({ entry }) => (
        <SaleAssetField
          saleAsset={entry}
          field="addOn"
          isDate
        />
      )
    },
    {
      key: 'soarrAdded',
      label: 'SOARR',
      hidden: 'pre' !== tableType,
      content: ({ entry }) => (
        <SaleAssetField
          saleAsset={entry}
          field="soarrAdded"
          isToggle
        />
      )
    },
    ...paddleServices.filter(paddleService => paddleService.presaleScreen).
      map(paddleService => ({
        key: paddleService.code,
        label: paddleService.code,
        hidden: 'pre' !== tableType,
        value: ({ entry }) => (
          entry.paddleServicesAdded?.includes(paddleService.code)
        ),
        content: ({ entry }) => (
          <SaleAssetField
            saleAsset={entry}
            field={paddleService.code}
            paddleServiceAddedField
            isToggle
          />
        )
      })),
    {
      key: 'location',
      label: 'Location',
      value: ({ entry }) => formattedLocation(entry.asset),
      content: ({ value }) => <Value>{value}</Value>,
    },
    {
      key: 'year',
      label: 'Year',
      value: ({ entry }) => entry.asset.year,
      content: ({ value }) => <Value>{value}</Value>,
    },
    {
      key: 'make',
      label: 'Make',
      cellStyle: { maxWidth: 160 },
      value: ({ entry }) => entry.asset.make,
      content: ({ value }) => <Value>{value}</Value>,
    },
    {
      key: 'model',
      label: 'Model',
      value: ({ entry }) => entry.asset.model,
      content: ({ value }) => <Value>{value}</Value>,
    },
    {
      key: 'vin',
      label: 'VIN',
      value: ({ entry }) => entry.asset.vin,
      content: ({ value }) => <Value>{value}</Value>,
    },
    {
      key: 'seller',
      label: 'Seller',
      value: ({ entry }) => entry.asset.seller.name,
      content: ({ entry }) => (
        <Link to={`/sellers/${entry.asset.seller.id}`}>
          <Value>{entry.asset.seller.name}</Value>
        </Link>
      )
    },
    {
      key: 'latestTitleLog',
      label: 'Title Status',
      value: ({ entry }) => entry.asset.latestTitleLog?.status,
      content: ({ entry, value }) => {
        return (
          <View
            style={{ display: 'flex', flexDirection: 'row' }}
          >
            <Value>
              {value}
            </Value>
            {value &&
              <Pressable
                onPress={() => {
                  setShowTitleLogHistory(true);
                  setClickedTitleLogHistory(entry);
                }}
              >
                <FontAwesomeIcon
                  icon={faClockRotateLeft}
                  title="View Title Log History"
                  style={{ marginLeft: '-0px', marginRight: '15px' }}
                />
              </Pressable>
            }
          </View>
        );
      },
    },
    {
      key: 'inpectionUrl',
      label: 'Inspection',
      hidden: 'pre' !== tableType,
      value: ({ entry }) => entry.asset.inspectionUrl,
      content: ({ value }) => (
        value && (
          <Value>
            <a href={value} target="_blank" rel="nofollow">
              {value.substr(0, 12)}...
            </a>
          </Value>
        )
      )
    },
    {
      key: 'bos',
      label: 'BOS',
      hidden: 'post' !== tableType,
      content: ({ entry }) => (
        <SaleAssetField
          saleAsset={entry}
          field="bos"
          isToggle
        />
      )
    },
    {
      key: 'release',
      label: 'R',
      hidden: 'post' !== tableType,
      content: ({ value }) => (
        value ? (
          <View>
            <FontAwesomeIcon
              icon={faCircleCheck}
              color="green"
              size="lg"
            />
          </View>
        ) : (
          <Value></Value>
        )
      )
    },
    {
      key: 'releaseOption',
      label: 'Release Option',
      headerStyle: { maxWidth: 70 },
      hidden: 'post' !== tableType,
      value: ({ entry }) => {
        if (entry.asset.seller.releaseOption?.includes('payment')) {
          return 'PAYMENT';
        } else if (entry.asset.seller.releaseOption?.includes('funded')) {
          return 'FUNDED';
        } else {
          return 'MANUAL';
        }
      },
      content: ({ value, entry }) => (
        <View style={{ flexDirection: 'row' }}>
          <Value style={{ color: 'MANUAL' === value ? 'red' : 'black' }}>
            {value}
          </Value>
          {'Sold' === entry.status && (
            <Pressable onPress={() => createRelease(entry.asset)}>
              <FontAwesomeIcon
                style={{ marginRight: 4 }}
                icon={faPaperPlane}
                title="Send Release"
              />
            </Pressable>
          )}
        </View>
      )
    },
    {
      key: 'releaseHold',
      label: 'Release Hold',
      headerStyle: { maxWidth: 70 },
      hidden: 'post' !== tableType,
      content: ({ entry }) => (
        <SaleAssetField
          saleAsset={entry}
          field="releaseHold"
          isToggle
          activeTrackColor="darkred"
        />
      )
    },
    {
      key: 'releaseConfirmed',
      label: 'Release Confirmed',
      hidden: 'post' !== tableType,
      headerStyle: { maxWidth: 90 },
      content: ({ entry }) => (
        <SaleAssetField
          saleAsset={entry}
          field="releaseConfirmed"
          isToggle
        />
      )
    },
    {
      key: 'invoiceNumber',
      label: 'Invoice #',
      hidden: 'post' !== tableType,
      headerStyle: { maxWidth: 80 },
      content: ({ entry }) => (
        <View style={{ flexDirection: 'row', gap: 6, alignItems: 'center' }}>
          <SaleAssetField
            saleAsset={entry}
            field="invoiceNumber"
            style={{ width: 70 }}
          />
          {secrets.app.doubleEntry && (
            entry.buyerNet && entry.activeOffer.buyer && (
              entry.invoiceNumber ? (
                entry.invoiceTxnId && (
                  <Pressable onPress={() => setShowInvoice(entry)}>
                    <FontAwesomeIcon
                      icon={faEye}
                      title="View Invoice"
                    />
                  </Pressable>
                )
              ) : (
                hasPolicy({ type: 'Txn', action: 'create' }) && (
                  <Pressable onPress={() => setShowNewInvoice(entry)}>
                    <FontAwesomeIcon
                      icon={faCirclePlus}
                      title="Create Invoice"
                    />
                  </Pressable>
                )
              )
            )
          )}
        </View>
      )
    },
    {
      key: 'buyerNet',
      label: 'Invoice Amount',
      hidden: 'post' !== tableType,
      headerStyle: { maxWidth: 70 },
      content: ({ entry }) => (
        <Value>
          {entry.activeOffer.soldPrice ? currency(entry.buyerNet) : ''}
        </Value>
      )
    },
    {
      key: 'validatedMethod',
      label: 'Validate',
      value: ({ entry }) => entry.activeOffer.validatedMethod,
      hidden: 'pre' === tableType,
      content: ({ entry }) => (
        <SaleAssetField
          saleAsset={entry}
          field="validatedMethod"
          selectOptions={['LVM', 'EMAIL', 'YES']}
          offer
        />
      )
    },
    {
      key: 'expectedYieldPrice',
      label: 'Expected Yield Price',
      headerStyle: { maxWidth: 60 },
      hidden: 'post' === tableType,
      value: ({ entry }) => parseFloat(entry.asset.expectedYieldPrice || 0),
      content: ({ entry }) => (
        <SaleAssetField
          saleAsset={entry}
          field="expectedYieldPrice"
          style={{ width: 80 }}
          isCurrency
          asset
        />
      )
    },
    {
      key: 'preBidStartPrice',
      label: 'Min Pre-Bid',
      headerStyle: { maxWidth: 60 },
      hidden: 'pre' !== tableType,
      value: ({ entry }) => parseFloat(entry.asset.preBidStartPrice || 0),
      content: ({ entry }) => (
        <SaleAssetField
          saleAsset={entry}
          field="preBidStartPrice"
          style={{ width: 80 }}
          isCurrency
        />
      )
    },
    {
      key: 'reservePrice',
      label: 'Reserve Price',
      headerStyle: { maxWidth: 70 },
      value: ({ value }) => parseFloat(value || 0),
      content: ({ entry, value }) => (
        'pre' === tableType ? (
          <SaleAssetField
            saleAsset={entry}
            field="reservePrice"
            style={{ width: 80 }}
            isCurrency
          />
        ) : (
          <Value>{currency(value)}</Value>
        )
      )
    },
    {
      key: 'reserveAbsolute-edit',
      label: 'Absolute',
      hidden: 'pre' !== tableType,
      value: ({ entry }) => entry.reserveAbsolute,
      content: ({ entry }) => (
        <SaleAssetField
          saleAsset={entry}
          field="reserveAbsolute"
          isToggle
        />
      )
    },
    {
      key: 'reserveAbsolute',
      label: 'Absolute',
      hidden: 'pre' === tableType,
      content: ({ value }) => (
        value ? (
          <View>
            <FontAwesomeIcon
              icon={faCircleCheck}
              color="green"
              size="lg"
            />
          </View>
        ) : (
          <Value></Value>
        )
      )
    },
    {
      key: 'price',
      label: 'Price',
      hidden: 'pre' === tableType,
      value: ({ entry }) => (
        entry.activeOffer.soldPrice || entry.activeOffer.price
      ),
      content: ({ entry, value }) => (
        ('Not Sold' === entry.status && !value) ? (
          <>
            <Value style={{ fontWeight: 'bold' }}>No Sale</Value>
            <Pressable
              onPress={() => setShowUnmarkNoSale(entry)}
              style={{ display: 'inline-block' }}
            >
              <FontAwesomeIcon
                icon={faEdit}
                title="Unmark No Sale"
                color={entry.invoiceNumber ? 'gray' : 'black'}
              />
            </Pressable>
          </>
        ) : (
          <View style={{ flexDirection: 'row', gap: 6 }}>
            <SaleAssetField
              saleAsset={entry}
              field="price"
              isCurrency
              offer
              style={{ width: 80 }}
            />
            <Pressable onPress={() => addToClipboard(entry, 'price', value)}>
              <FontAwesomeIcon
                icon={faCopy}
                title="Copy to Clipboard"
                color={
                  (entry.clipboardPrice &&
                    entry.clipboardPrice.at + 5000 > new Date().getTime()
                  ) ? 'green' : 'black'
                }
              />
            </Pressable>
            {entry.activeOffer.isIf && (
              <Value style={{ fontWeight: 'bold' }}>- IF</Value>
            )}
          </View>
        )
      )
    },
    {
      key: 'paddleNumber',
      label: 'Paddle Number',
      hidden: 'pre' === tableType,
      value: ({ entry }) => entry.activeOffer?.paddleNumber,
      content: ({ entry }) => (
        <View style={{ flexDirection: 'row', alignItems: 'baseline' }}>
          <SaleAssetField
            saleAsset={entry}
            field="paddleServiceId"
            paddleServices={paddleServices}
            offer
          />
          <Text>-</Text>
          <SaleAssetField
            saleAsset={entry}
            field="paddleNumber"
            offer
            style={{ width: 60 }}
          />
        </View>
      )
    },
    {
      key: 'sellerFunctions',
      label: 'post' === tableType ? 'Status' : 'Seller Functions',
      hidden: 'pre' === tableType,
      cellStyle: { minWidth: 100 },
      value: ({ entry }) => {
        if (['Unwound', 'Removed'].includes(entry.status)) {
          return entry.status;
        } else if ('Declined' === entry.activeOffer && entry.counterPrice) {
          return 'Declined (Buyer)';
        } else if (entry.activeOffer.result) {
          return entry.activeOffer.result;
        } else if (entry.activeOffer.counterPrice) {
          return `Counter ${currency(entry.activeOffer.counterPrice)}`;
        } else {
          return null;
        }
      },
      content: ({ entry, value }) => (
        (
          entry.activeOffer.isIf && entry.activeOffer.price && !value &&
          'live' === tableType
        ) ? (
          <BidFunctions
            saleAsset={entry}
            setShowSaleHistory={setShowSaleHistory}
          />
        ) : (
          <Value>{value}</Value>
        )
      )
    },
    {
      key: 'buyerFunctions',
      label: 'Buyer Functions',
      hidden: 'live' !== tableType,
      cellStyle: { minWidth: 100 },
      value: ({ entry }) => entry.activeOffer.result,
      content: ({ entry, value }) => (
        (entry.activeOffer.counterPrice && !value) ? (
          <BidFunctions
            saleAsset={entry}
            setShowSaleHistory={setShowSaleHistory}
            buyer
          />
        ) : (
          null
        )
      )
    },
    {
      key: 'history',
      label: '',
      sort: 'prevent',
      cellStyle: { minWidth: 104 },
      content: ({ entry }) => (
        <Cell>
          <Pressable
            onPress={() => setShowSaleHistory(entry)}
            style={{ display: 'inline-block' }}
          >
            <FontAwesomeIcon
              icon={faClockRotateLeft}
              size="lg"
              title="View Asset/Offer History"
            />
          </Pressable>
          <Text>{'  '}</Text>
          <Pressable
            onPress={() => setShowEditStatus(entry)}
            style={{ display: 'inline-block' }}
          >
            <FontAwesomeIcon
              icon={faEdit}
              title="Edit Status"
              color={entry.invoiceNumber ? 'gray' : 'black'}
            />
          </Pressable>
          <Text>{'  '}</Text>
          <ResourceNotifications resource={entry} />
          <Text>{'  '}</Text>
          <Pressable
            onPress={() => setShowPaddleServiceBids(entry)}
            style={{ display: 'inline-block' }}
          >
            <FontAwesomeIcon
              icon={faSquareList}
              size="lg"
              title="View Paddle Service Bids"
            />
          </Pressable>
        </Cell>
      )
    },
    {
      key: 'buyerFees',
      label: 'Buy Fee',
      hidden: 'pre' === tableType,
      value: ({ value }) => parseFloat(value),
      content: ({ entry }) => (
        <Value>{currency(entry.buyerFees)}</Value>
      )
    },
    {
      key: 'sellerFees',
      label: 'Fees Paid by Seller',
      cellStyle: { minWidth: 100 },
      hidden: 'pre' === tableType,
      value: ({ value }) => parseFloat(value),
      content: ({ entry }) => (
        <Cell>
          <Text>{currency(entry.sellerFees)}</Text>
          <Pressable
            onPress={() => setShowSellerFees(entry)}
            style={{ display: 'inline-block' }}
          >
            <FontAwesomeIcon
              icon={faEdit}
              size="lg"
              title="View/Edit Fees Detail"
            />
          </Pressable>
        </Cell>
      )
    },
    {
      key: 'sellerPrice',
      label: 'Seller Price',
      headerStyle: { maxWidth: 60 },
      hidden: 'pre' === tableType,
      content: ({ entry }) => (
        <SaleAssetField
          saleAsset={entry}
          field="sellerPrice"
          style={{ width: 80 }}
          isCurrency
        />
      )
    },
    {
      key: 'sellerNet',
      label: 'Seller Net ACH',
      hidden: 'post' !== tableType,
      headerStyle: { maxWidth: 60 },
      content: ({ entry }) => (
        <Cell>
          <Value>{currency(entry.sellerNet)}</Value>
          {'Sold' === entry.status && !entry.sellerAchOn &&
            hasPolicy({ type: 'Txn', action: 'create' }) &&
            (
              <Pressable
                style={{ display: 'inline-block' }}
                onPress={() => addToBatch(entry)}
              >
                <FontAwesomeIcon
                  icon={faMoneyCheck}
                  size="lg"
                  title="Add to ACH Batch"
                />
              </Pressable>
            )}

          {secrets.app.doubleEntry && parseFloat(entry.sellerNet) < 0 && (
            <View style={{ marginLeft: 6 }}>
              {entry.sellerInvoiceTxnId ? (
                <Pressable
                  onPress={() => (
                    setShowInvoice({ ...entry, sellerInvoice: true })
                  )}
                >
                  <FontAwesomeIcon
                    icon={faEye}
                    title="View Invoice"
                  />
                </Pressable>
              ) : (
                <Pressable
                  onPress={() => (
                    setShowNewInvoice({ ...entry, sellerInvoice: true })
                  )}
                >
                  <FontAwesomeIcon
                    icon={faCirclePlus}
                    title="Create Invoice"
                  />
                </Pressable>
              )}
            </View>
          )}
        </Cell>
      )
    },
    {
      key: 'sellerAchInstRec',
      label: 'ACH Inst Rec',
      hidden: 'post' !== tableType,
      headerStyle: { maxWidth: 50 },
      content: ({ entry }) => (
        <SaleAssetField
          saleAsset={entry}
          field="sellerAchInstRec"
          style={{ width: 60 }}
        />
      )
    },
    {
      key: 'sellerAchOn',
      label: 'Date of Seller ACH',
      hidden: 'post' !== tableType,
      headerStyle: { maxWidth: 60 },
      content: ({ entry }) => (
        <SaleAssetField
          saleAsset={entry}
          field="sellerAchOn"
          isDate
        />
      )
    },
    {
      key: 'buyerId',
      label: 'Buyer',
      value: ({ entry }) => entry.activeOffer?.buyer?.name,
      cellStyle: { minWidth: 200 },
      hidden: 'pre' === tableType,
      content: ({ entry }) => (
        <BuyerAutocomplete
          key={`${entry?.id || moment() / 1000}-buyerId-field`}
          buyer={entry.activeOffer?.buyer}
          placeHolder={entry.activeOffer?.buyer?.displayName}
          resource="Buyer"
          name="buyerId"
          clickedResult={clickedResult}
          setClickedResult={setClickedResult}
          setShowNewBuyerModal={setShowNewBuyerModal}
          saleAsset={entry}
          setSaleAssetId={setSaleAssetId}
          updateSaleAsset={updateSaleAsset}
        />
      )
    },
    {
      key: 'buyerContact',
      label: 'Buyer Contact',
      hidden: 'pre' === tableType,
      value: ({ entry }) => {
        const buyer = entry.activeOffer.buyer;

        if (buyer) {
          return (
            [
              (buyer.companyName && buyer.name ? buyer.companyName : null),
              formattedLocation(buyer),
              buyer.email,
              buyer.phoneNumber?.formatted
            ].filter(detail => detail).join(' | ')
          );
        } else {
          return '';
        }
      },
      content: ({ value }) => <Value>{value}</Value>
    },
    {
      key: 'buyerWireAmount',
      label: 'Buyer Amount of Wire',
      hidden: 'post' !== tableType,
      headerStyle: { maxWidth: 70 },
      content: ({ entry }) => (
        <SaleAssetField
          saleAsset={entry}
          field="buyerWireAmount"
          style={{ width: 70 }}
          isCurrency
        />
      )
    },
    {
      key: 'buyerWireOn',
      label: 'Date of Wire',
      hidden: 'post' !== tableType,
      headerStyle: { maxWidth: 50 },
      content: ({ entry }) => (
        <SaleAssetField
          saleAsset={entry}
          field="buyerWireOn"
          isDate
        />
      )
    },
    {
      key: 'incomingWireTitle',
      label: 'Incoming Wire Title',
      hidden: 'post' !== tableType,
      content: ({ entry }) => (
        <SaleAssetField
          saleAsset={entry}
          field="incomingWireTitle"
          style={{ width: 300 }}
        />
      )
    },
    {
      key: 'notes',
      label: 'Notes',
      hidden: 'pre' === tableType,
      content: ({ entry }) => (
        <SaleAssetField
          saleAsset={entry}
          field="notes"
          style={{ width: 400 }}
        />
      )
    },
    {
      key: 'pre-functions',
      label: 'Functions',
      hidden: 'pre' !== tableType,
      sort: 'prevent',
      content: ({ entry }) => {
        if (hasPolicy({ type: 'SaleAsset', action: 'destroy' })) {
          if (['Unwound', 'Removed'].includes(entry.status)) {
            return (
              <Value>Unwound</Value>
            );
          } else {
            return (
              <Pressable onPress={() => handleRemove(entry)}>
                <Value>Remove from this Sale</Value>
              </Pressable>
            );
          }
        } else {
          return null;
        }
      }
    },
  ];

  const entries = saleAssets.map(saleAsset => {
    const clipboardData = clipboard[saleAsset.id];

    const clipboardPrice = 'price' === clipboardData?.type ?
      clipboardData : null;

    const clipboardLotOrder = 'lotOrder' === clipboardData?.type ?
      clipboardData : null;

    const clipboardStockNumber = 'stockNumber' === clipboardData?.type ?
      clipboardData : null;

    const isLinked = linkedSaleAssetId === saleAsset.id;

    return {
      ...saleAsset,
      lotNumberSort: isPublicAuctionPreSale ? (saleAsset.manualRunOrder ?? null) : saleAsset.lotNumberSort,
      clipboardPrice,
      clipboardLotOrder,
      clipboardStockNumber,
      isLinked
    };
  });

  const tableRenderKey = entries.map(entry => `${entry.id}:${entry.manualRunOrder || ''}`).join('-');

  return (
    <View>
      {isPublicAuctionPreSale && (
        <RunOrderBar>
          <RunOrderText>
            Enter the run order for each lot. Changes save on blur or when you press Enter.
          </RunOrderText>
        </RunOrderBar>
      )}

      <TableContainer className="sales-process">
        <DataTable.Simple
          key={tableRenderKey}
          data={entries}
          columns={columns}
          pageLength="All"
          styles={TABLE_STYLES}
          htmlTable
          sortOnOrderChangeOnly={false}
          sortOnArrowClickOnly
          memoizeRows={false}
        />
      </TableContainer>

      {showNewBuyerModal &&
        <Modal
          showModal={showNewBuyerModal}
          setShowModal={setShowNewBuyerModal}
          modalType={'Create Buyer'}
          objectName={clickedResult?.name}
        >
          <NewBuyer
            setShowModal={setShowNewBuyerModal}
            buyerName={clickedResult.name}
            saleAssetId={saleAssetId}
            updateSaleAsset={updateSaleAsset}
          />
        </Modal>
      }
      {showNewInvoice &&
        <Modal
          showModal={showNewInvoice}
          setShowModal={setShowNewInvoice}
          modalType="Create Invoice"
          objectName={showNewInvoice.asset.stockNumber}
        >
          <NewInvoice
            saleAsset={showNewInvoice}
            sellerInvoice={showNewInvoice.sellerInvoice}
            setShowModal={setShowNewInvoice}
            onCompleted={txn => {
              if (showNewInvoice.sellerInvoice) {
                setShowInvoice({ ...showNewInvoice, sellerInvoiceTxnId: txn.id });
              } else {
                setShowInvoice({ ...showNewInvoice, invoiceTxnId: txn.id });
              }

              setShowNewInvoice(false);
            }}
          />
        </Modal>
      }
      {showInvoice &&
        <Modal
          showModal={showInvoice}
          setShowModal={setShowInvoice}
          modalType="Invoice Detail"
          objectName={showInvoice.asset.stockNumber}
        >
          <Invoice
            id={
              showInvoice.sellerInvoice ?
                showInvoice.sellerInvoiceTxnId :
                showInvoice.invoiceTxnId
            }
            saleAsset={showInvoice}
            setShowModal={setShowInvoice}
          />
        </Modal>
      }
      {showSellerFees &&
        <Modal
          showModal={showSellerFees}
          setShowModal={setShowSellerFees}
          modalType="Fees Detail"
          objectName={showSellerFees.asset.stockNumber}
        >
          <Fees asset={showSellerFees.asset} fromSalesProcess={true} />
        </Modal>
      }
      {showSaleHistory &&
        <Modal
          showModal={showSaleHistory}
          setShowModal={setShowSaleHistory}
          modalType="Bid History"
          objectName={showSaleHistory.asset.stockNumber}
        >
          <SaleHistory
            asset={showSaleHistory.asset}
            saleAsset={showSaleHistory}
          />
        </Modal>
      }
      {showEditStatus &&
        <Modal
          showModal={showEditStatus}
          setShowModal={setShowEditStatus}
          modalType="Edit Bid Status"
          objectName={showEditStatus.asset.stockNumber}
        >
          <EditStatus
            saleAsset={showEditStatus}
            setShowModal={setShowEditStatus}
          />
        </Modal>
      }
      {showPaddleServiceBids &&
        <Modal
          showModal={showPaddleServiceBids}
          setShowModal={setShowPaddleServiceBids}
          modalType="Paddle Service Bids"
          objectName={showPaddleServiceBids.lotNumber}
        >
          <PaddleServiceBids
            asset={showPaddleServiceBids.asset}
            saleAsset={showPaddleServiceBids}
          />
        </Modal>
      }
      {showTitleLogHistory &&
        <Modal
          setShowModal={setShowTitleLogHistory}
          asset={clickedTitleLogHistory.asset}
          modalType={'Title Log History'}
          style={{ marginRight: 0 }}
        >
          <TitleLogHistory asset={clickedTitleLogHistory.asset} />
        </Modal>
      }
      {showUnmarkNoSale &&
        <Modal
          setShowModal={setShowUnmarkNoSale}
          modalType="Remove No Sale"
        >
          <UnmarkNoSale
            saleAsset={showUnmarkNoSale}
            updateSaleAsset={updateSaleAsset}
            setShowModal={setShowUnmarkNoSale}
          />
        </Modal>
      }
    </View>
  );
};

export default SaleProcess;